import React, { useState, useEffect, useCallback, forwardRef, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  IconButton,
  Tooltip,
  useToast,
  Select,
  Container,
  Switch,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Stack,
  Input,
  InputGroup,
  InputLeftElement,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useBreakpointValue,
  FormControl,
  FormLabel,
  HStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay
} from "@chakra-ui/react";
import { AddIcon, ViewIcon, DeleteIcon, EditIcon, SearchIcon, CalendarIcon } from "@chakra-ui/icons";
import { api } from "../services/api"; 
import { useNavigate } from "react-router-dom";
import Preloader from "../components/Preloader";

import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ptBR from 'date-fns/locale/pt-BR';
registerLocale('pt-BR', ptBR);

const ListaPromocoes = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  const [listas, setListas] = useState([]);
  const [stats, setStats] = useState({ listas_ativas: 0, produtos_ativos: 0 });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});

  const [deleteId, setDeleteId] = useState(null);
  const cancelRef = useRef();

  const [filtros, setFiltros] = useState({
    status: 'todas',
    origem: 'todas',
    titulo: '',
    data_inicio: null,
    data_fim: null
  });

  const isMobile = useBreakpointValue({ base: true, lg: false });

  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <InputGroup onClick={onClick} ref={ref}>
      <InputLeftElement pointerEvents="none">
        <CalendarIcon color="gray.300" />
      </InputLeftElement>
      <Input 
        value={value} 
        placeholder="Selecione o período..." 
        bg="gray.50" 
        readOnly 
        cursor="pointer"
      />
    </InputGroup>
  ));

  const fetchListas = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page,
        status: filtros.status,
        origem: filtros.origem,
        titulo: filtros.titulo,
        data_inicio: filtros.data_inicio ? filtros.data_inicio.toISOString().split('T')[0] : '',
        data_fim: filtros.data_fim ? filtros.data_fim.toISOString().split('T')[0] : ''
      });

      const response = await api.get(`/lista-descontos?${params.toString()}`);
      
      if (response.data.listas) {
        setListas(response.data.listas.data);
        setPagination({
            current_page: response.data.listas.current_page,
            last_page: response.data.listas.last_page,
            total: response.data.listas.total
        });
        setStats(response.data.stats);
      } 
      else if (response.data.data) {
        setListas(response.data.data);
      }

    } catch (error) {
      console.error(error);
      toast({
        title: "Erro ao carregar dados",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [filtros, toast]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchListas(1);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [filtros, fetchListas]);

  const handleFilterChange = (field, value) => {
    setFiltros(prev => ({ ...prev, [field]: value }));
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setFiltros(prev => ({
        ...prev,
        data_inicio: start,
        data_fim: end
    }));
  };

  const requestDelete = (id) => {
      setDeleteId(id);
  }

  const confirmDelete = async () => {
      if (!deleteId) return;
      setDeleteId(null);
      setLoading(true);
      try {
          await api.delete(`/lista-descontos/${deleteId}`);
          
          setStats(prev => ({
              ...prev,
              listas_ativas: prev.listas_ativas > 0 ? prev.listas_ativas - 1 : 0
          }));

          toast({ title: "Lista excluída", status: "success" });
          fetchListas(pagination.current_page);
      } catch (error) {
          toast({ title: "Erro ao excluir", status: "error" });
          setLoading(false);
      }
  }

  const handleToggleStatus = async (lista) => {
      const novoStatus = lista.status === 'ativa' ? 'desativada' : 'ativa';
      setLoading(true);

      try {
          await api.put(`/lista-descontos/${lista.id}/status`, { status: novoStatus });
          toast({ title: `Lista ${novoStatus === 'ativa' ? 'Ativada' : 'Desativada'}`, status: "success", duration: 1000 });
          await fetchListas(pagination.current_page);
      } catch (error) {
          console.error(error);
          toast({ title: "Erro ao alterar status", status: "error" });
          setLoading(false);
      }
  }

  const getOrigemLabel = (val) => {
    const map = {
        '0': 'TAGA', '1': 'MORELATE', '2': 'VANNUCCI', '3': 'ISAPA',
        '4': 'BRCOMPANY', '5': 'AUTOLATINA', '6': 'LNG', '7': 'CBA', '8': 'MSAM', '9': 'BLPARTS', '100': 'OUTROS'
    };
    return map[val] || val;
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "ativa": return <Badge colorScheme="green" px={2} borderRadius="full">Ativa</Badge>;
      case "pausada": return <Badge colorScheme="yellow" px={2} borderRadius="full">Pausada</Badge>;
      case "desativada": return <Badge colorScheme="red" px={2} borderRadius="full">Desativada</Badge>;
      case "concluida": return <Badge colorScheme="gray" px={2} borderRadius="full">Concluída</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  const renderTable = () => (
    <Box overflowX="auto" borderRadius="lg" border="1px solid" borderColor="gray.200">
        <Table variant="simple" bg="white">
            <Thead bg="gray.50">
            <Tr>
                <Th>ID</Th>
                <Th>Título</Th>
                <Th>Origem</Th>
                <Th isNumeric>Itens</Th>
                <Th>Vigência</Th>
                <Th>Status</Th>
                <Th width="150px">Ações</Th>
            </Tr>
            </Thead>
            <Tbody>
            {listas.map((lista) => (
                <Tr key={lista.id} _hover={{ bg: "gray.50" }}>
                <Td fontWeight="bold" color="gray.600">#{lista.id}</Td>
                <Td fontWeight="semibold">{lista.titulo}</Td>
                <Td>
                    <Badge colorScheme="purple" variant="subtle">{getOrigemLabel(lista.origem)}</Badge>
                </Td>
                <Td isNumeric fontWeight="bold">{lista.quantidade_itens}</Td>
                <Td fontSize="sm">
                    {new Date(lista.data_inicio).toLocaleDateString()} 
                    {lista.data_fim ? ` até ${new Date(lista.data_fim).toLocaleDateString()}` : ""}
                </Td>
                <Td>{getStatusBadge(lista.status)}</Td>
                <Td>
                    <Flex align="center" gap={3}>
                        <Tooltip label={lista.status === 'ativa' ? 'Desativar' : 'Ativar'} placement="top">
                            <Box display="inline-block">
                                <Switch 
                                    isChecked={lista.status === 'ativa'} 
                                    onChange={() => handleToggleStatus(lista)}
                                    colorScheme="green"
                                    size="md"
                                />
                            </Box>
                        </Tooltip>
                        
                        <Tooltip label="Ver Detalhes">
                            <IconButton icon={<ViewIcon />} size="sm" colorScheme="blue" variant="ghost" onClick={() => navigate(`/promocoes/${lista.id}`)} />
                        </Tooltip>

                        <Tooltip label="Editar">
                            <IconButton icon={<EditIcon />} size="sm" colorScheme="orange" variant="ghost" onClick={() => navigate(`/promocoes/${lista.id}/editar`)} />
                        </Tooltip>

                        <Tooltip label="Excluir">
                            <IconButton icon={<DeleteIcon />} size="sm" colorScheme="red" variant="ghost" onClick={() => requestDelete(lista.id)} />
                        </Tooltip>
                    </Flex>
                </Td>
                </Tr>
            ))}
            </Tbody>
        </Table>
    </Box>
  );

  const renderCards = () => (
      <SimpleGrid columns={1} spacing={4}>
          {listas.map((lista) => (
              <Card key={lista.id} shadow="sm" border="1px solid" borderColor="gray.200">
                  <CardHeader pb={2}>
                      <Flex justify="space-between" align="center">
                          <Heading size="sm" color="gray.700">#{lista.id} - {lista.titulo}</Heading>
                          {getStatusBadge(lista.status)}
                      </Flex>
                  </CardHeader>
                  <CardBody py={2}>
                      <Stack spacing={2} fontSize="sm">
                          <Flex justify="space-between">
                              <Text color="gray.500">Origem:</Text>
                              <Text fontWeight="bold">{getOrigemLabel(lista.origem)}</Text>
                          </Flex>
                          <Flex justify="space-between">
                              <Text color="gray.500">Itens:</Text>
                              <Text fontWeight="bold">{lista.quantidade_itens}</Text>
                          </Flex>
                          <Flex justify="space-between">
                              <Text color="gray.500">Vigência:</Text>
                              <Text>{new Date(lista.data_inicio).toLocaleDateString()} {lista.data_fim ? `- ${new Date(lista.data_fim).toLocaleDateString()}` : ""}</Text>
                          </Flex>
                      </Stack>
                  </CardBody>
                  <CardFooter pt={2} justify="space-between" align="center" borderTop="1px solid #f0f0f0">
                      <Flex align="center" gap={2}>
                          <Text fontSize="xs" fontWeight="bold" color="gray.600">Ativa?</Text>
                          <Switch 
                            isChecked={lista.status === 'ativa'} 
                            onChange={() => handleToggleStatus(lista)} 
                            colorScheme="green" 
                            size="sm"
                          />
                      </Flex>
                      <HStack spacing={1}>
                          <IconButton icon={<ViewIcon />} size="sm" variant="ghost" colorScheme="blue" onClick={() => navigate(`/promocoes/${lista.id}`)} />
                          <IconButton icon={<EditIcon />} size="sm" variant="ghost" colorScheme="orange" onClick={() => navigate(`/promocoes/${lista.id}/editar`)} />
                          <IconButton icon={<DeleteIcon />} size="sm" variant="ghost" colorScheme="red" onClick={() => requestDelete(lista.id)} />
                      </HStack>
                  </CardFooter>
              </Card>
          ))}
      </SimpleGrid>
  );

  return (
    <Box bg="gray.50" minH="100vh" py={6}>
      <Container maxW="container.xl">
        
        <Flex justify="space-between" align="center" mb={6} direction={{ base: "column", md: "row" }} gap={4}>
          <Box>
            <Heading size="lg" color="orange.500">Listas de Promoções</Heading>
          </Box>
          <Button 
              leftIcon={<AddIcon />} 
              colorScheme="orange" 
              size="lg"
              onClick={() => navigate("/promocoes/nova")}
              w={{ base: "100%", md: "auto" }}
              boxShadow="md"
          >
            Nova Lista
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
            <Card direction="row" align="center" p={4} variant="outline" bg="white" borderLeft="4px solid" borderColor="green.400">
                <Stat>
                    <StatLabel color="gray.500">Listas Ativas</StatLabel>
                    <StatNumber color="green.500">{stats.listas_ativas || 0}</StatNumber>
                    <StatHelpText>Campanhas rodando</StatHelpText>
                </Stat>
            </Card>
            <Card direction="row" align="center" p={4} variant="outline" bg="white" borderLeft="4px solid" borderColor="blue.400">
                <Stat>
                    <StatLabel color="gray.500">Produtos em Promoção</StatLabel>
                    <StatNumber color="blue.500">{stats.produtos_ativos || 0}</StatNumber>
                    <StatHelpText>Total de SKUs ativos</StatHelpText>
                </Stat>
            </Card>
        </SimpleGrid>

        <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} alignItems="end">
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>Busca</FormLabel>
                    <InputGroup>
                        <InputLeftElement pointerEvents="none"><SearchIcon color="gray.300" /></InputLeftElement>
                        <Input 
                            placeholder="Título da lista..." 
                            value={filtros.titulo}
                            onChange={(e) => handleFilterChange('titulo', e.target.value)}
                            bg="gray.50"
                        />
                    </InputGroup>
                </FormControl>

                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>Origem</FormLabel>
                    <Select value={filtros.origem} onChange={(e) => handleFilterChange('origem', e.target.value)} bg="gray.50">
                        <option value="todas">Todas Origens</option>
                        <option value="0">TAGA</option>
                        <option value="1">MORELATE</option>
                        <option value="2">VANNUCCI</option>
                        <option value="3">ISAPA</option>
                        <option value="4">BRCOMPANY</option>
                        <option value="5">AUTOLATINA</option>
                        <option value="6">LNG</option>
                        <option value="7">CBA</option>
                        <option value="8">MSAM</option>
                        <option value="9">BLPARTS</option>
                        <option value="100">OUTROS</option>
                    </Select>
                </FormControl>

                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>Status</FormLabel>
                    <Select value={filtros.status} onChange={(e) => handleFilterChange('status', e.target.value)} bg="gray.50">
                        <option value="todas">Todos Status</option>
                        <option value="ativa">Ativas</option>
                        <option value="pausada">Pausadas</option>
                        <option value="desativada">Desativadas</option>
                        <option value="concluida">Concluídas</option>
                    </Select>
                </FormControl>

                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>Período de Vigência</FormLabel>
                    <DatePicker
                        selectsRange={true}
                        startDate={filtros.data_inicio}
                        endDate={filtros.data_fim}
                        onChange={handleDateRangeChange}
                        isClearable={true}
                        locale="pt-BR"
                        dateFormat="dd/MM/yyyy"
                        customInput={<CustomDateInput />}
                    />
                </FormControl>
            </SimpleGrid>
        </Box>

        {loading ? (
            <Flex justify="center" py={20}><Preloader /></Flex>
        ) : (
            <>
                {listas.length === 0 ? (
                    <Flex bg="white" p={10} justify="center" align="center" borderRadius="lg" border="1px dashed" borderColor="gray.300" direction="column">
                        <Text fontSize="lg" color="gray.500" mb={4}>Nenhuma lista encontrada com os filtros atuais.</Text>
                        <Button variant="outline" onClick={() => setFiltros({status: 'todas', origem: 'todas', titulo: '', data_inicio: null, data_fim: null})}>
                            Limpar Filtros
                        </Button>
                    </Flex>
                ) : (
                    isMobile ? renderCards() : renderTable()
                )}

                {pagination.total > 10 && (
                    <Flex justify="space-between" align="center" mt={6}>
                        <Button 
                            isDisabled={pagination.current_page === 1} 
                            onClick={() => fetchListas(pagination.current_page - 1)}
                            size="sm"
                            variant="outline"
                        >
                            Anterior
                        </Button>
                        <Text fontSize="sm" color="gray.600">
                            Página <b>{pagination.current_page}</b> de <b>{pagination.last_page}</b>
                        </Text>
                        <Button 
                            isDisabled={pagination.current_page === pagination.last_page} 
                            onClick={() => fetchListas(pagination.current_page + 1)}
                            size="sm"
                            variant="outline"
                        >
                            Próxima
                        </Button>
                    </Flex>
                )}
            </>
        )}

        <AlertDialog isOpen={!!deleteId} leastDestructiveRef={cancelRef} onClose={() => setDeleteId(null)}>
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        Excluir Lista
                    </AlertDialogHeader>

                    <AlertDialogBody>
                        Tem certeza que deseja excluir esta lista de promoção? Essa ação não pode ser desfeita.
                    </AlertDialogBody>

                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={() => setDeleteId(null)}>
                            Cancelar
                        </Button>
                        <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                            Excluir
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>

      </Container>
    </Box>
  );
};

export default ListaPromocoes;